import * as React from 'react';
import Image from 'next/image';
import { Typography } from '@components/typography';
import { Link } from '@components/link';
import { formatWholeDollarAmount } from '@utils/money';
import { capitalizeFirstLetter } from '@utils/string-helpers';
import { OtaInfoMap, ComparisonQuoteResult, isSuccessfulComparisonQuote} from '../types';
import { AnalyticsRef } from '@components/analytics-ref';
import { useQuotes } from '../../quotes-context';

type ComparisonQuoteItemProps = {
  comparisonQuote: ComparisonQuoteResult;
  otaUrl: string;
  otaInfo: OtaInfoMap;
  afterTaxes?: boolean;
};

const ComparisonQuoteItem: React.FC<ComparisonQuoteItemProps> = ({
  comparisonQuote,
  otaUrl,
  otaInfo,
  afterTaxes = false,
}) => {
  const { quoteData } = useQuotes();

  const otaSource = comparisonQuote.externalSource as keyof typeof otaInfo;
  const otaData = otaInfo[otaSource];

  const priceCents = isSuccessfulComparisonQuote(comparisonQuote)
    ? comparisonQuote.priceCents
    : undefined;

  const savvyPriceCents = quoteData?.bookingQuoteByListingAndDates.__typename === 'BookingQuote' ?
    (afterTaxes ?
      quoteData.bookingQuoteByListingAndDates.totalRecoverableCostCents :
      quoteData.bookingQuoteByListingAndDates.totalWithoutTaxesCents)
    : undefined;

  return (
    <div className="flex justify-between">
      <div className="flex w-full justify-between">
        {isSuccessfulComparisonQuote(comparisonQuote) && !afterTaxes ? (
          <AnalyticsRef name="ota_quote">
            <Link
              className="flex items-top space-x-[18px]"
              rel="noopener noreferrer"
              target="_blank"
              to={otaUrl}
            >
              <div className="bg-white h-10 w-10 flex items-center justify-center rounded-md">
                {otaData?.icon && (
                  <Image
                    src={otaData.icon}
                    alt={comparisonQuote.externalSource ?? ''}
                    width={32}
                    height={29}
                    className="rounded-md object-contain max-w-[32px] max-h-[29px]"
                  />
                )}
              </div>
              <Typography variant="body2">
                {capitalizeFirstLetter(comparisonQuote.externalSource ?? '')}
              </Typography>
            </Link>
          </AnalyticsRef>
        ) : (
          <div className="flex items-top space-x-[18px]">
            <div className="bg-white h-10 w-10 flex items-center justify-center rounded-md">
              {otaData?.icon && (
                <Image
                  src={otaData.icon}
                  alt={comparisonQuote.externalSource ?? ''}
                  width={32}
                  height={29}
                  className="rounded-md object-contain max-w-[32px] max-h-[29px]"
                />
              )}
            </div>
            <Typography variant="body2">
              {capitalizeFirstLetter(comparisonQuote.externalSource ?? '')}
            </Typography>
          </div>
        )}
        {savvyPriceCents !== undefined && (
          <div className="flex flex-col">
            {priceCents ? (
              <>
                <Typography
                  variant="body2"
                  className="text-primary-main text-right text-sm"
                >
                  {priceCents > savvyPriceCents
                    ? `${comparisonQuote.externalSource === 'airbnb' ? 'Yikes' : 'Ouch'} ${formatWholeDollarAmount(
                        priceCents - savvyPriceCents
                      )} more!`
                    : `${comparisonQuote.externalSource === 'airbnb' ? 'Hmmm' : 'Noted'}, ${formatWholeDollarAmount(
                        savvyPriceCents - priceCents
                      )} less`}
                </Typography>
                <Typography
                  variant="small"
                  className="text-primary-main text-right"
                >
                  {`${formatWholeDollarAmount(priceCents)} ${
                    afterTaxes ? 'after' : 'before'
                  } taxes`}
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  variant="body2"
                  className="text-primary-main text-right text-sm"
                >
                  {`Not found on ${capitalizeFirstLetter(comparisonQuote.externalSource ?? '')}`}
                </Typography>
                <Typography
                  variant="small"
                  className="text-primary-main text-right"
                >
                  Price comparison unavailable
                </Typography>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonQuoteItem;
